"use client";

import { useState } from "react";
import { Check, CheckCircle2, Copy, Globe, LifeBuoy, Lightbulb, RefreshCw } from "lucide-react";
import { ErrorNotice } from "@/components/ui";
import { errorMessage, mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";

interface DomainResponse {
  domain: { hostname: string; verificationToken: string; verifiedAt?: string } | null;
  cname?: string;
}

function CopyValue({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="cd-value">
      <code>{value}</code>
      <button
        type="button"
        className="icon-button"
        aria-label={copied ? "Copied" : `Copy ${label}`}
        title={copied ? "Copied" : `Copy ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}

function Step({ n, state, title, children }: { n: number; state: "done" | "active" | "todo"; title: string; children?: React.ReactNode }) {
  return (
    <li className={`cd-step is-${state}`}>
      <span className="cd-step-dot" aria-hidden="true">{state === "done" ? <Check size={14} /> : n}</span>
      <div className="cd-step-body">
        <h3>{title}</h3>
        {children}
      </div>
    </li>
  );
}

function Help({ host }: { host: string }) {
  return (
    <details className="cd-help">
      <summary><LifeBuoy size={15} /> Stuck? Common fixes</summary>
      <dl>
        <dt>Verify says a record is missing</dt>
        <dd>Check for typos and that both records exist. Some providers add your domain twice (<code>{host}.example.com</code>). Enter only the part before your root domain, like <code>play</code>.</dd>
        <dt>Using Cloudflare</dt>
        <dd>Set the CNAME to <b>DNS only</b> (grey cloud) until the padlock works. If you turn the proxy on later, set SSL/TLS mode to <b>Full (strict)</b>.</dd>
        <dt>Browser shows a certificate warning</dt>
        <dd>Wait a minute and reload. If you have CAA records, they must allow <code>letsencrypt.org</code> and <code>sectigo.com</code>.</dd>
        <dt>Using a root domain like example.com</dt>
        <dd>Root domains can&apos;t hold a CNAME. Use a subdomain such as <code>play.example.com</code>, or a DNS provider with CNAME flattening.</dd>
        <dt>It stopped working later</dt>
        <dd>Your Company plan must stay active and both DNS records must stay in place.</dd>
      </dl>
    </details>
  );
}

export function CustomDomain({ eligible, reason }: { eligible: boolean; reason: string }) {
  const resource = useResource<DomainResponse>(eligible ? "/billing/domain" : null);
  const [hostname, setHostname] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const domain = resource.data?.domain ?? null;
  const verified = !!domain?.verifiedAt;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy("save");
    setError("");
    setNote("");
    try {
      await mutation("/billing/domain", { hostname: hostname.trim() }, "PUT");
      setHostname("");
      resource.refresh();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy("");
    }
  }

  async function verify() {
    setBusy("verify");
    setError("");
    setNote("");
    try {
      await mutation("/billing/domain/verify", {});
      setNote("Verified. A certificate is issued the first time someone visits your domain.");
      resource.refresh();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusy("");
    }
  }

  const cname = resource.data?.cname ?? "";
  const current = !domain ? 1 : !verified ? 3 : 4;

  return (
    <section className="cd-card">
      <div className="cd-head">
        <Globe size={18} />
        <div>
          <h2>Custom domain</h2>
          <p className="muted">Serve your workspace from your own domain, with a free trusted SSL certificate.</p>
        </div>
        {domain ? <span className={`cd-badge ${verified ? "is-ok" : "is-wait"}`}>{verified ? "Live" : "Waiting for DNS"}</span> : null}
      </div>

      {!eligible ? (
        <p className="cd-locked">{reason}</p>
      ) : (
        <>
          <ErrorNotice message={error || resource.error} />
          {note ? <p className="cd-ok" role="status"><CheckCircle2 size={15} /> {note}</p> : null}

          <ol className="cd-steps">
            <Step n={1} state={domain ? "done" : "active"} title={domain ? `Domain: ${domain.hostname}` : "Choose your domain"}>
              <form className="cd-form" onSubmit={save}>
                <div className="cd-row">
                  <input
                    id="cd-hostname"
                    aria-label="Your domain"
                    value={hostname}
                    onChange={(e) => setHostname(e.target.value)}
                    placeholder="play.example.com"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={253}
                  />
                  <button type="submit" className="button button-primary" disabled={busy !== "" || hostname.trim() === ""}>
                    {busy === "save" ? "Saving…" : domain ? "Change domain" : "Connect domain"}
                  </button>
                </div>
                <p className="muted cd-hint">
                  {domain ? "Changing the domain removes verification, so you will add the records again." : "Use a subdomain like play.example.com. Root domains (example.com) usually can’t use a CNAME."}
                </p>
              </form>
            </Step>

            <Step n={2} state={!domain ? "todo" : verified ? "done" : "active"} title="Add two DNS records">
              {domain ? (
                <>
                  <p className="muted cd-hint">Add these at your DNS provider (Cloudflare, Namecheap, GoDaddy, Route 53…). Click the copy icon next to each value.</p>
                  <div className="cd-records">
                    <div className="cd-record">
                      <span className="cd-type">CNAME</span>
                      <div className="cd-field"><small>Name</small><CopyValue value={domain.hostname} label="CNAME name" /></div>
                      <div className="cd-field"><small>Points to</small><CopyValue value={cname} label="CNAME value" /></div>
                    </div>
                    <div className="cd-record">
                      <span className="cd-type">TXT</span>
                      <div className="cd-field"><small>Name</small><CopyValue value={`_velnqo.${domain.hostname}`} label="TXT name" /></div>
                      <div className="cd-field"><small>Value</small><CopyValue value={domain.verificationToken} label="TXT value" /></div>
                    </div>
                  </div>
                  <p className="cd-tip"><Lightbulb size={14} /> Many providers only want the part before your root domain in Name, e.g. <code>play</code> and <code>_velnqo.play</code>. Don&apos;t add a trailing dot.</p>
                </>
              ) : (
                <p className="muted cd-hint">We&apos;ll show your two records here after you connect a domain.</p>
              )}
            </Step>

            <Step n={3} state={!domain ? "todo" : verified ? "done" : "active"} title="Verify DNS">
              {domain ? (
                <>
                  <p className="muted cd-hint">DNS changes can take a minute to an hour to appear.</p>
                  <div className="cd-actions">
                    <button type="button" className="button button-primary" disabled={busy !== ""} onClick={verify}>
                      <RefreshCw size={15} /> {busy === "verify" ? "Checking DNS…" : verified ? "Check again" : "Verify DNS"}
                    </button>
                  </div>
                </>
              ) : null}
            </Step>

            <Step n={4} state={current === 4 ? "done" : "todo"} title="SSL is automatic">
              <p className="muted cd-hint">
                {verified && domain
                  ? <>Your workspace is live at <a href={`https://${domain.hostname}`} target="_blank" rel="noreferrer">https://{domain.hostname}</a>. The certificate is issued on the first visit (5–15 seconds) and renews itself.</>
                  : "Once verified, a free trusted certificate is issued the first time someone opens your domain. Nothing to install."}
              </p>
            </Step>
          </ol>

          <Help host={domain?.hostname ?? "play"} />
        </>
      )}
    </section>
  );
}
